import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useCachedFetch } from '../hooks/useCachedFetch';
import { LECTURES_API, RESEARCH_API, STORAGE_BASE, lectureImg, lectureFileUrl, LECTURE_GRADIENTS, trunc } from '../utils/api';

function Skel({ style }) { return <div className="skel" style={style} />; }

const NO_MSG = {
  lectures:{ ar:'لا توجد محاضرات بهذه اللغة حالياً', en:'No lectures available in this language yet.', ur:'اس زبان میں ابھی کوئی لیکچر دستیاب نہیں ہے۔' },
  research:{ ar:'لا توجد أبحاث بهذه اللغة حالياً',  en:'No research available in this language yet.',  ur:'اس زبان میں ابھی کوئی تحقیق دستیاب نہیں ہے۔'  },
};

const LANG_LABELS = {
  ar: { label: 'العربية', flag: '🇸🇦' },
  en: { label: 'English', flag: '🇬🇧' },
  ur: { label: 'اردو',    flag: '🇵🇰' },
};

/** Apply the correct language version to a lecture, falling back to Arabic */
function applyVersion(lecture, lang) {
  const translations = lecture.translations || {};
  const v = translations[lang] || translations['ar'] || {};
  return {
    ...lecture,
    title:       v.title       || lecture.title,
    description: v.description || lecture.description,
    video_url:   v.video_url   || lecture.video_url,
    youtube_url: v.youtube_url || lecture.youtube_url,
    youtube_embed: v.youtube_embed || lecture.youtube_embed,
  };
}

// ─── Lectures list page ───────────────────────────────────────────────────
export function LecturesPage({ lang }) {
  const { t } = useLang();
  const navigate = useNavigate();
  // No language= param — apply version client-side
  const { data: raw, loading } = useCachedFetch(`${LECTURES_API}?page=1`);
  const lectures = (Array.isArray(raw) ? raw : (raw?.data || []))
    // Only show a lecture if it has content in the requested language:
    // - Arabic: always show (root record is always ar)
    // - EN/UR: only if a real translation exists for that language
    .filter(l => lang === 'ar' || (l.translations && l.translations[lang]?.title))
    .map(l => applyVersion(l, lang));

  return(
    <div id="page-lectures">
      <div className="page-hero">
        <div className="geo-lg"/><div className="page-hero-glow"/>
        <div className="pw">
          <div className="breadcrumb">
            <a href="/" onClick={e=>{e.preventDefault();navigate('/');}}>
              {t('nav.home.label')}
            </a>
            <span>/</span><span>{t('lec.title1')}</span>
          </div>
          <h1><em>{t('lec.title1')}</em> {t('lec.title2')}</h1>
          <p>{t('lec.subtitle')}</p>
        </div>
      </div>
      <div className="pw" style={{padding:'1.5rem 0'}}>
        <div className="media-row" style={{gridTemplateColumns:'repeat(3,1fr)',gap:'1.2rem'}}>
          {loading&&[1,2,3,4,5,6].map(i=>(
            <div key={i} className="mcard" style={{pointerEvents:'none'}}>
              <div className="mcard-thumb"><Skel style={{width:'100%',height:'100%'}}/></div>
              <div className="mcard-body"><Skel style={{width:'80%',height:16,marginBottom:'.5rem'}}/></div>
            </div>
          ))}
          {!loading&&lectures.length===0&&(
            <div style={{gridColumn:'1/-1',textAlign:'center',padding:'3rem',color:'var(--ink3)'}}>
              <div style={{fontSize:'2.5rem',marginBottom:'.75rem'}}>🎬</div>
              <p>{NO_MSG.lectures[lang]||NO_MSG.lectures.ar}</p>
            </div>
          )}
          {!loading&&lectures.length>0&&lectures.map((l,i)=>{
            const img=lectureImg(l); const file=lectureFileUrl(l);
            return(
              <div key={l.id} className="mcard" onClick={()=>navigate(`/lectures/${l.id}`)} style={{cursor:'pointer'}}>
                <div className="mcard-thumb" style={img?{backgroundImage:`url('${img}')`,backgroundSize:'cover',backgroundPosition:'center'}:{background:LECTURE_GRADIENTS[i%LECTURE_GRADIENTS.length]}}>
                  {!img&&<div className="play-btn">▶</div>}
                </div>
                <div className="mcard-body">
                  <div className="mcard-title">{trunc(l.title||'…',80)}</div>
                  <div className="mcard-meta">{l.speaker||t('label.lecture.item')}</div>
                  {(l.lecture_date||l.location)&&(
                    <div style={{fontSize:'.72rem',color:'var(--ink3)',marginTop:'.25rem',display:'flex',gap:'.6rem',flexWrap:'wrap'}}>
                      {l.lecture_date&&<span>📅 {new Date(l.lecture_date).toLocaleDateString('ar-SA',{year:'numeric',month:'long',day:'numeric'})}</span>}
                      {l.location&&<span>📍 {l.location}</span>}
                    </div>
                  )}
                  {l.description&&<div style={{fontSize:'.75rem',color:'var(--ink3)',marginTop:'.3rem',lineHeight:1.5}}>{trunc(l.description,70)}</div>}
                  {file&&<a href={file} target="_blank" rel="noreferrer" className="scf-btn" style={{display:'inline-block',marginTop:'.5rem',fontSize:'.78rem'}} onClick={e=>e.stopPropagation()}>{t('label.open.file')}</a>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Single lecture page ──────────────────────────────────────────────────
export function SingleLecturePage({ lecture, lectures }) {
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  if(!lecture)return null;

  // Apply the correct language version
  const display = applyVersion(lecture, lang);

  // Only show switcher buttons for languages that have a translation
  const translations = lecture.translations || {};
  const availableLangs = Object.keys(translations).filter(l => l !== lang && translations[l]?.title);

  const idx=lectures.indexOf(lecture);
  const bg=LECTURE_GRADIENTS[idx>=0?idx%LECTURE_GRADIENTS.length:0];

  // Resolve file and video for the current language
  const file = display.video_url || lectureFileUrl(lecture);
  const youtubeEmbed = display.youtube_embed;

  return(
    <div id="page-lecture-single">
      <div className="page-hero" style={{minHeight:200,background:bg}}>
        <div className="geo-lg"/><div className="page-hero-glow"/>
        <div className="pw">
          <div className="breadcrumb">
            <a href="/" onClick={e=>{e.preventDefault();navigate('/');}}>
              {t('nav.home.label')}
            </a>
            <span>/</span>
            <a href="/lectures" onClick={e=>{e.preventDefault();navigate('/lectures');}}>
              {t('lec.title1')}
            </a>
            <span>/</span><span>{trunc(display.title||'…',50)}</span>
          </div>
          <h1 style={{fontSize:'clamp(1.2rem,3vw,1.8rem)',lineHeight:1.5}}>{display.title}</h1>
          <p style={{opacity:.75,fontSize:'var(--t-sm)',marginTop:'.4rem'}}>{lecture.speaker||''}</p>

          {/* ── Language switcher buttons ── */}
          {availableLangs.length > 0 && (
            <div style={{display:'flex',gap:'.6rem',marginTop:'1rem',flexWrap:'wrap',alignItems:'center'}}>
              <span style={{fontSize:'var(--t-sm)',color:'rgba(255,255,255,.7)',fontWeight:600}}>
                🌐 {lang==='ar' ? 'شاهد بلغة أخرى:' : lang==='ur' ? 'دوسری زبان میں دیکھیں:' : 'Watch in:'}
              </span>
              {availableLangs.map(targetLang => (
                <button
                  key={targetLang}
                  onClick={() => setLang(targetLang)}
                  style={{
                    display:'inline-flex',alignItems:'center',gap:'.35rem',
                    padding:'.4rem .9rem',borderRadius:'2rem',
                    border:'2px solid rgba(255,255,255,.8)',
                    background:'transparent',color:'#fff',
                    fontWeight:700,fontSize:'var(--t-sm)',cursor:'pointer',
                    transition:'background .15s, color .15s',
                  }}
                  onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,.2)';}}
                  onMouseLeave={e=>{e.currentTarget.style.background='transparent';}}
                >
                  {LANG_LABELS[targetLang]?.flag} {LANG_LABELS[targetLang]?.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="pw" style={{padding:'1.5rem 0 3rem'}}>
        <div className="lecture-viewer-grid">

          {/* ── Left column: video / PDF viewer + About box ── */}
          <div style={{display:'flex',flexDirection:'column',gap:'1.2rem'}}>

            {/* YouTube embed takes priority if available */}
            {youtubeEmbed ? (
              <div className="lecture-pdf-panel">
                <div className="lpdf-toolbar">
                  <div className="lpdf-toolbar-left">
                    <span style={{fontSize:'.85rem',fontFamily:'var(--fam-display)',opacity:.85}}>{t('lecsin.viewer.label')}</span>
                  </div>
                  <div className="lpdf-toolbar-right">
                    {display.youtube_url&&<a href={display.youtube_url} target="_blank" rel="noreferrer" className="lpdf-btn">{t('label.open.new.tab')}</a>}
                  </div>
                </div>
                <div className="lpdf-canvas-wrap">
                  <iframe
                    src={youtubeEmbed}
                    style={{width:'100%',height:480,border:'none',borderRadius:6,boxShadow:'0 4px 24px rgba(0,0,0,.12)',display:'block'}}
                    title={display.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            ) : (
              <div className="lecture-pdf-panel">
                <div className="lpdf-toolbar">
                  <div className="lpdf-toolbar-left">
                    <span style={{fontSize:'.85rem',fontFamily:'var(--fam-display)',opacity:.85}}>{t('lecsin.viewer.label')}</span>
                  </div>
                  <div className="lpdf-toolbar-right">
                    {file&&<a href={file} target="_blank" rel="noreferrer" className="lpdf-btn">{t('label.download.file')}</a>}
                    {file&&<a href={file} target="_blank" rel="noreferrer" style={{background:'rgba(255,255,255,.15)',color:'#fff',borderRadius:7,padding:'.3rem .75rem',fontSize:'.82rem',fontFamily:'var(--fam-display)',fontWeight:600,textDecoration:'none',whiteSpace:'nowrap'}}>{t('label.open.new.tab')}</a>}
                  </div>
                </div>
                <div className="lpdf-canvas-wrap">
                  {file
                    ?<iframe src={`${file}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`} style={{width:'100%',height:780,border:'none',borderRadius:6,boxShadow:'0 4px 24px rgba(0,0,0,.12)',display:'block'}} title={display.title}/>
                    :<div style={{textAlign:'center',padding:'2rem'}}>
                      <div style={{fontSize:'3rem',marginBottom:'.75rem'}}>📄</div>
                      <p style={{color:'var(--ink3)'}}>{t('lecsin.no.file')}</p>
                      <button className="btn-primary" style={{marginTop:'1rem'}} onClick={()=>navigate('/lectures')}>{t('lecsin.back')}</button>
                    </div>
                  }
                </div>
              </div>
            )}

            {/* About box */}
            <div className="single-sbox">
              <div className="ssb-head">{t('lecsin.about.title')}</div>
              <div style={{padding:'1.2rem'}}>
                <div style={{fontFamily:'var(--fam-display)',fontSize:'var(--t-md)',color:'var(--ink)',fontWeight:700,marginBottom:'.6rem'}}>{display.title}</div>
                {lecture.speaker&&<div style={{fontSize:'var(--t-sm)',color:'var(--ink3)',marginBottom:'.3rem'}}>🎤 {lecture.speaker}</div>}
                {lecture.lecture_date&&<div style={{fontSize:'var(--t-sm)',color:'var(--ink3)',marginBottom:'.3rem'}}>📅 {new Date(lecture.lecture_date).toLocaleDateString('ar-SA',{year:'numeric',month:'long',day:'numeric'})}</div>}
                {lecture.location&&<div style={{fontSize:'var(--t-sm)',color:'var(--ink3)',marginBottom:'.3rem'}}>📍 {lecture.location}</div>}
                {display.description&&<div style={{fontSize:'var(--t-sm)',color:'var(--ink2)',lineHeight:1.7,marginTop:'.6rem'}}>{display.description}</div>}
                {file&&<a href={file} target="_blank" rel="noreferrer" className="scf-btn" style={{display:'inline-block',marginTop:'1rem',width:'100%',textAlign:'center'}}>{t('label.download.file')}</a>}
              </div>
            </div>
          </div>

          {/* ── Right sidebar ── */}
          <aside className="lecture-viewer-aside">

            {/* Available translations box */}
            {availableLangs.length > 0 && (
              <div className="single-sbox">
                <div className="ssb-head">
                  🌐 {lang==='ar' ? 'النسخ المتاحة' : lang==='ur' ? 'دستیاب ترجمے' : 'Available Translations'}
                </div>
                <div style={{padding:'1rem',display:'flex',flexDirection:'column',gap:'.5rem'}}>
                  {availableLangs.map(targetLang => (
                    <button
                      key={targetLang}
                      onClick={() => setLang(targetLang)}
                      style={{
                        display:'flex',alignItems:'center',justifyContent:'center',gap:'.5rem',
                        padding:'.55rem',borderRadius:'8px',
                        border:'2px solid var(--g1)',background:'transparent',
                        color:'var(--g1)',fontWeight:700,fontSize:'var(--t-sm)',
                        cursor:'pointer',width:'100%',transition:'background .15s, color .15s',
                      }}
                      onMouseEnter={e=>{e.currentTarget.style.background='var(--g1)';e.currentTarget.style.color='#fff';}}
                      onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color='var(--g1)';}}
                    >
                      {LANG_LABELS[targetLang]?.flag} {LANG_LABELS[targetLang]?.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="single-sbox">
              <div className="ssb-head">{t('lecsin.others.title')}</div>
              <div className="sbox-body">
                {lectures.filter(x=>x.id!==lecture.id).slice(0,6).map(r=>{
                  const rd = applyVersion(r, lang);
                  return(
                    <div key={r.id} className="slist-item" onClick={()=>navigate(`/lectures/${r.id}`)} style={{cursor:'pointer'}}>
                      <div className="slist-n" style={{background:'var(--g1)',color:'#fff',borderRadius:6,padding:'.15rem .45rem',fontSize:'.72rem',flexShrink:0}}>▶</div>
                      <a href={`/lectures/${r.id}`} onClick={e=>e.preventDefault()}>{trunc(rd.title||'…',55)}</a>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="hadith-widget">
              <div className="geo-sm"/>
              <div className="title">{t('lecsin.hadith2.title')}</div>
              <p>{t('lecsin.hadith2.text')}</p>
              <div className="ref">{t('lecsin.hadith2.ref')}</div>
            </div>
          </aside>

        </div>
      </div>
    </div>
  );
}

// ─── Research page — unchanged logic ─────────────────────────────────────
export function ResearchPage({ lang }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const { data: raw, loading } = useCachedFetch(`${RESEARCH_API}?page=1&language=${lang}`);
  // Only show papers that have a title in the CURRENT language. Papers that
  // exist only in another language (e.g. an English-only paper whose Arabic
  // root has no title) are hidden here and appear on that language's page.
  const research=(Array.isArray(raw)?raw:(raw?.data||[])).filter(r=>r.language===lang&&(r.title||'').trim());

  return(
    <div id="page-research">
      <div className="page-hero">
        <div className="geo-lg"/><div className="page-hero-glow"/>
        <div className="pw">
          <div className="breadcrumb">
            <a href="/" onClick={e=>{e.preventDefault();navigate('/');}}>
              {t('nav.home.label')}
            </a>
            <span>/</span><span>{t('res.title1')}</span>
          </div>
          <h1><em>{t('res.title1')}</em> {t('res.title2')}</h1>
          <p>{t('res.subtitle')}</p>
        </div>
      </div>
      <div className="pw" style={{padding:'1.5rem 0'}}>
        <div className="media-row" style={{gridTemplateColumns:'repeat(3,1fr)',gap:'1.2rem'}}>
          {loading&&[1,2,3].map(i=>(
            <div key={i} className="mcard" style={{pointerEvents:'none'}}>
              <div className="mcard-thumb"><Skel style={{width:'100%',height:'100%'}}/></div>
              <div className="mcard-body"><Skel style={{width:'80%',height:16}}/></div>
            </div>
          ))}
          {!loading&&research.length===0&&(
            <div style={{gridColumn:'1/-1',textAlign:'center',padding:'3rem',color:'var(--ink3)'}}>
              <div style={{fontSize:'2.5rem'}}>📄</div>
              <p style={{marginTop:'.75rem'}}>{NO_MSG.research[lang]||NO_MSG.research.ar}</p>
            </div>
          )}
          {!loading&&research.length>0&&research.map((r,i)=>{
            const img=r.cover_image?(r.cover_image.startsWith('http')?r.cover_image:STORAGE_BASE+r.cover_image):'';
            const file=r.file_url?(r.file_url.startsWith('http')?r.file_url:STORAGE_BASE+r.file_url):'';
            return(
              <div key={r.id} className="mcard" onClick={()=>navigate(`/research/${r.id}`)} style={{cursor:'pointer'}}>

                <div className="mcard-thumb" style={img?{backgroundImage:`url('${img}')`,backgroundSize:'cover',backgroundPosition:'center'}:{background:LECTURE_GRADIENTS[i%LECTURE_GRADIENTS.length]}}>
                  {!img&&<div className="play-btn">📄</div>}
                </div>
                <div className="mcard-body">
                  <div className="mcard-title">{trunc(r.title||'…',80)}</div>
                  <div className="mcard-meta">{[r.authors,r.category].filter(Boolean).join(' · ')||t('label.research.item')}</div>
                  {r.abstract&&<div style={{fontSize:'.75rem',color:'var(--ink3)',marginTop:'.3rem',lineHeight:1.5}}>{trunc(r.abstract,70)}</div>}
                  {file&&<a href={file} target="_blank" rel="noreferrer" className="scf-btn" style={{display:'inline-block',marginTop:'auto',paddingTop:'.5rem',fontSize:'.78rem'}}>{t('label.download.research')}</a>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}